import { AuthCardProvider } from "@/features/auth/context/AuthCardContext";
import type { PropsWithChildren } from "react";

import { BrandWordmark } from "@/shared/components/branding/BrandWordmark";

export type AuthEntryCardVariant =
  | "login"
  | "forgot"
  | "password"
  | "otp-code"
  | "otp-contact"
  | "identity"
  | "country"
  | "entry"
  | "register"
  | "biometric"
  | "success";

type AuthEntryCardProps = PropsWithChildren<{
  variant?: AuthEntryCardVariant;
  cardClassName?: string;
  contentClassName?: string;
  hideLogo?: boolean;
}>;

const BASE_CARD_CLASSES =
  "w-[min(calc(100%_+_var(--layout-auth-overlap)),var(--layout-panel-width))] min-h-[min(44.75rem,calc(100svh_-_16rem))] rounded-sm px-16 py-[2.875rem] pb-11 shadow-md lg:w-[min(100%,var(--layout-panel-width))] lg:min-h-auto lg:rounded-sm lg:px-8 lg:py-10 lg:pb-10 lg:shadow-md sm:w-full sm:min-h-screen sm:rounded-none sm:shadow-none sm:px-5 sm:py-8";

const WIDE_CARD_CLASSES =
  "w-[100%] max-w-[780px] min-h-[min(44rem,calc(100svh-8rem))] px-8 py-14 mx-auto overflow-hidden rounded-sm sm:w-full sm:max-w-full sm:min-h-[min(40rem,calc(100svh-8rem))] sm:px-6 sm:py-10 sm:rounded-none md:w-[120%] md:max-w-[700px] md:min-h-[min(42rem,calc(100svh-8rem))] md:px-8 md:py-12 md:rounded-sm lg:w-[130%] lg:max-w-[1100px] lg:min-h-[min(38rem,calc(100svh-8rem))] lg:px-10 lg:py-12 lg:rounded-md";

const MEDIUM_CARD_CLASSES =
  "w-[min(100%,67.25rem)] min-h-[min(44rem,calc(100svh-8rem))] px-12 py-10 pb-7 mx-auto overflow-hidden rounded-sm sm:w-full sm:max-w-full sm:min-h-[min(40rem,calc(100svh-8rem))] sm:px-6 sm:py-10 sm:rounded-none md:w-[120%] md:max-w-[700px] md:min-h-[min(42rem,calc(100svh-8rem))] md:px-8 md:py-12 md:rounded-sm lg:w-[130%] lg:max-w-[1100px] lg:min-h-[min(38rem,calc(100svh-8rem))] lg:px-10 lg:py-12 lg:rounded-md";

const BIOMETRIC_CARD_CLASSES =
  "w-[min(100%,67.25rem)] min-h-[min(40.5rem,calc(100svh_-_6rem))] px-12 py-10 pb-7 lg:px-8 lg:py-9 lg:pb-6";

const SUCCESS_CARD_CLASSES =
  "w-[min(100%,43rem)] min-h-[min(45rem,calc(100svh_-_6rem))] px-12 py-10 pb-9 lg:px-8 lg:py-8 lg:pb-6";

const VARIANT_CARD_CLASSES: Record<AuthEntryCardVariant, string> = {
  login: WIDE_CARD_CLASSES,
  forgot: WIDE_CARD_CLASSES,
  password: WIDE_CARD_CLASSES,
  "otp-code": WIDE_CARD_CLASSES,
  "otp-contact": WIDE_CARD_CLASSES,
  register: WIDE_CARD_CLASSES,
  identity: MEDIUM_CARD_CLASSES,
  country: MEDIUM_CARD_CLASSES,
  entry: MEDIUM_CARD_CLASSES,
  biometric: BIOMETRIC_CARD_CLASSES,
  success: SUCCESS_CARD_CLASSES,
};

const COMPACT_BUTTON_VARIANTS = new Set<AuthEntryCardVariant>([
  "login",
  "forgot",
  "password",
  "otp-code",
  "otp-contact",
  "identity",
  "country",
  "entry",
  "register",
]);

export function AuthEntryCard({
  variant,
  cardClassName = "",
  children,
  contentClassName = "",
  hideLogo = false,
}: AuthEntryCardProps) {
  const wrapperClassName = [
    "flex flex-col items-center bg-surface-card lg:shadow-lg",
    variant ? VARIANT_CARD_CLASSES[variant] : BASE_CARD_CLASSES,
    cardClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const className = ["w-full flex flex-col items-center", contentClassName]
    .filter(Boolean)
    .join(" ");
  const buttonSize =
    variant && COMPACT_BUTTON_VARIANTS.has(variant) ? "compact" : "default";

  return (
    <AuthCardProvider buttonSize={buttonSize}>
      <div className={wrapperClassName}>
        {!hideLogo && (
          <header className="w-full flex justify-center">
            <BrandWordmark />
          </header>
        )}
        <div className={className}>{children}</div>
      </div>
    </AuthCardProvider>
  );
}
